/**
 * Normalise every supported input into one async stream of chunks.
 *
 * Reads Blob / File lazily (a 1 MiB window at a time), so the file is never held whole in memory.
 * Detection is duck-typed instead of `instanceof` so a jsdom Blob and a Node Blob both work.
 */

import type { CsvSource } from "./types";

type Chunk = Uint8Array | string;

/** Size of the window used when a Blob has no `.stream()` (jsdom, old browsers). */
const BLOB_WINDOW = 1024 * 1024;

function isBlobLike(value: unknown): value is Blob {
  const v = value as Partial<Blob> | null;
  return (
    typeof v === "object" &&
    v !== null &&
    typeof v.slice === "function" &&
    typeof v.size === "number" &&
    typeof v.type === "string"
  );
}

function isReadableStream(value: unknown): value is ReadableStream<Chunk> {
  return typeof value === "object" && value !== null && typeof (value as ReadableStream).getReader === "function";
}

async function readSlice(slice: Blob): Promise<Uint8Array> {
  if (typeof slice.arrayBuffer === "function") return new Uint8Array(await slice.arrayBuffer());
  return await new Promise<Uint8Array>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("Could not read the file."));
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.readAsArrayBuffer(slice);
  });
}

async function* streamChunks(stream: ReadableStream<Chunk>): AsyncGenerator<Chunk> {
  const reader = stream.getReader();
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) return;
      if (value !== undefined) yield value;
    }
  } finally {
    // Runs on early exit too (preview, abort, parse error): stop the producer and free the lock.
    try {
      await reader.cancel();
    } catch {
      /* already closed */
    }
    reader.releaseLock();
  }
}

async function* blobChunks(blob: Blob): AsyncGenerator<Chunk> {
  if (typeof blob.stream === "function") {
    yield* streamChunks(blob.stream() as ReadableStream<Chunk>);
    return;
  }
  for (let offset = 0; offset < blob.size; offset += BLOB_WINDOW) {
    yield await readSlice(blob.slice(offset, Math.min(offset + BLOB_WINDOW, blob.size)));
  }
}

export async function* chunksOf(source: CsvSource): AsyncGenerator<Chunk> {
  if (typeof source === "string") {
    yield source;
    return;
  }
  if (ArrayBuffer.isView(source)) {
    yield new Uint8Array(source.buffer, source.byteOffset, source.byteLength);
    return;
  }
  if (Object.prototype.toString.call(source) === "[object ArrayBuffer]") {
    yield new Uint8Array(source as ArrayBuffer);
    return;
  }
  if (isBlobLike(source)) {
    yield* blobChunks(source);
    return;
  }
  if (isReadableStream(source)) {
    yield* streamChunks(source);
    return;
  }
  if (Symbol.asyncIterator in (source as object)) {
    for await (const chunk of source as AsyncIterable<Chunk>) yield chunk;
    return;
  }
  for (const chunk of source as Iterable<Chunk>) yield chunk;
}
